import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { Modal, Input, Button } from '@/components/ui'
import { useAuthStore } from '@/stores/auth'
import { useReloginDialogStore } from '@/stores/relogin-dialog'
import { useRememberMeStore } from '@/stores/remember-me'
import { useRelogin } from '@/hooks/use-relogin'
import { reloginSchema, type ReloginFormValues } from './schema'

export function ReloginDialog() {
  const isOpen = useReloginDialogStore((s) => s.isOpen)
  const close = useReloginDialogStore((s) => s.close)
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const navigate = useNavigate()
  const remembered = useRememberMeStore.getState()
  const rememberedPassword = remembered.enabled ? remembered.password : ''

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReloginFormValues>({
    resolver: zodResolver(reloginSchema),
    defaultValues: { password: rememberedPassword },
  })

  const { mutate: relogin, isPending } = useRelogin({
    onSuccess: () => {
      toast.success('Login realizado com sucesso!')
      reset()
      close()
      window.location.reload()
    },
    onError: () => {
      toast.error('E-mail ou senha inválidos')
    },
  })

  useEffect(() => {
    if (!isOpen) reset()
  }, [isOpen, reset])

  if (!isOpen || !user) return null

  const handleCancel = () => {
    reset()
    close()
    clearAuth()
    navigate({ to: '/' })
  }

  const onSubmit = ({ password }: ReloginFormValues) => {
    relogin({ email: user.mail, password })
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title="Reautenticação necessária"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-5"
        noValidate
        aria-label="Formulário de reautenticação"
        data-test-id="relogin-form"
      >
        <p className="text-sm text-text-primary m-0">
          Houve uma alteração no sistema que requer que você faça login novamente para acessar as informações atualizadas.
        </p>

        <p className="text-sm text-text-primary m-0">
          Informe a senha para:
          <br />
          <b>{user.mail}</b>
        </p>

        <Input
          label="Senha"
          type="password"
          autoComplete="current-password"
          placeholder="Digite sua senha"
          leftIcon={<Lock size={18} strokeWidth={1.75} />}
          showToggle
          error={errors.password?.message}
          data-test-id="relogin-input-password"
          {...register('password')}
        />

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={handleCancel}
            data-test-id="relogin-btn-cancel"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isPending}
            data-test-id="relogin-btn-submit"
          >
            Entrar
          </Button>
        </div>
      </form>
    </Modal>
  )
}
